import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import apiService from '../services/apiService';
import { LoadingSpinner, StarRating } from '../components';

const STATUS_COLOR = {
  pending: '#F59E0B', new: '#F59E0B', accepted: '#3B82F6',
  preparing: '#8B5CF6', ready: '#10B981', completed: '#10B981',
  delivered: '#10B981', cancelled: '#EF4444', rejected: '#EF4444',
};
const STATUS_LABEL = {
  pending: 'Order Placed', new: 'Order Placed', accepted: 'Accepted',
  preparing: 'Preparing', ready: 'Ready for Pickup', completed: 'Completed',
  delivered: 'Delivered', cancelled: 'Cancelled', rejected: 'Rejected',
};

function fmt(amount) {
  const n = parseFloat(amount);
  return isNaN(n) ? '$0.00' : `$${n.toFixed(2)}`;
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return 'N/A'; }
}

const PROGRESS_STEPS = [
  { key: 'pending', label: 'Placed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'ready', label: 'Ready' },
  { key: 'completed', label: 'Done' },
];

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { reorderItems } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [foodRating, setFoodRating] = useState(0);
  const [restaurantRating, setRestaurantRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    apiService.getOrderById(orderId).then(res => {
      const orderData = res?.order || res?.data?.order || (res?.data && typeof res.data === 'object' ? res.data : null);
      setOrder(orderData);
    }).catch(console.error).finally(() => setLoading(false));

    // Poll for status updates on active orders
    const poll = setInterval(async () => {
      const res = await apiService.getOrderById(orderId);
      const orderData = res?.order || res?.data?.order || res?.data;
      if (orderData) setOrder(orderData);
    }, 30000);
    return () => clearInterval(poll);
  }, [orderId]);

  const handleReorder = () => {
    if (!order?.items?.length) return;
    reorderItems(
      order.items.map(i => ({
        id: i.menu_item_id || i.item_id || i.id,
        name: i.item_name || i.name,
        price: parseFloat(i.unit_price || i.price || 0),
        quantity: i.quantity,
        kitchenId: order.kitchen_id,
      })),
      { id: order.kitchen_id, name: order.kitchen_name }
    );
    navigate('/customer/cart');
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { alert('Please enter a reason for cancellation'); return; }
    setCancelLoading(true);
    try {
      const res = await apiService.cancelOrder(orderId, cancelReason);
      if (res?.success) {
        setOrder(prev => ({ ...prev, status: 'cancelled' }));
        setShowCancelModal(false);
      } else {
        alert(res?.error || 'Could not cancel order.');
      }
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!foodRating || !restaurantRating) { alert('Please rate both food and restaurant'); return; }
    setRatingLoading(true);
    try {
      const res = await apiService.submitOrderRating(orderId, {
        food_rating: foodRating,
        restaurant_rating: restaurantRating,
      });
      if (res?.success !== false) {
        setRatingSubmitted(true);
        setShowRatingModal(false);
        alert('Thank you for your feedback!');
      }
    } catch (e) { } finally {
      setRatingLoading(false);
    }
  };

  if (loading) return <div className="page-container"><LoadingSpinner text="Loading order..." /></div>;
  if (!order) return (
    <div className="page-container">
      <div className="empty-state">
        <div className="empty-icon">📦</div>
        <h3>Order not found</h3>
        <button className="btn-primary" onClick={() => navigate('/customer/orders')}>View Orders</button>
      </div>
    </div>
  );

  const status = order.status || 'pending';
  const isCancelled = ['cancelled', 'rejected'].includes(status);
  const isCompleted = ['completed', 'delivered'].includes(status);
  const isActive = ['new','pending','accepted','preparing','ready'].includes(status);
  const canCancel = ['new','pending'].includes(status);
  const currentStepIdx = PROGRESS_STEPS.findIndex(s => s.key === status || (status === 'new' && s.key === 'pending'));

  return (
    <div className="order-detail-page">
      <div className="order-detail-header">
        <button className="back-btn" onClick={() => navigate('/customer/orders')}>← Back</button>
        <h2 className="page-title">Order #{order.order_number || orderId}</h2>
      </div>

      {/* Status Progress */}
      {!isCancelled && (
        <div className="progress-bar-container">
          {PROGRESS_STEPS.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div className={`progress-step ${idx <= currentStepIdx ? 'done' : ''} ${idx === currentStepIdx ? 'active' : ''}`}>
                <div className="progress-dot">
                  {idx < currentStepIdx ? '✓' : idx + 1}
                </div>
                <span className="progress-label">{step.label}</span>
              </div>
              {idx < PROGRESS_STEPS.length - 1 && (
                <div className={`progress-connector ${idx < currentStepIdx ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="order-detail-content">
        {/* Status Badge */}
        <div className="order-status-section">
          <span
            className="order-status-large"
            style={{ background: (STATUS_COLOR[status] || '#888') + '20', color: STATUS_COLOR[status] || '#888' }}
          >
            {STATUS_LABEL[status] || status}
          </span>
          <p className="order-date">Placed on {formatDate(order.created_at)}</p>
        </div>

        {/* Pickup PIN */}
        {['preparing', 'ready'].includes(status) && order.pickup_pin && (
          <div className="pickup-pin-section">
            <h4>🔐 Pickup PIN</h4>
            <div className="pickup-pin">{order.pickup_pin}</div>
            <p className="pickup-pin-hint">Show this PIN when you arrive to pick up your order</p>
          </div>
        )}

        {/* Kitchen Info */}
        <div className="order-detail-section">
          <h3 className="order-detail-section-title">🍽️ Kitchen</h3>
          <p className="order-kitchen">{order.kitchen_name || 'Kitchen'}</p>
          {order.pickup_time_start && (
            <p className="order-pickup-time">
              Pickup: {new Date(order.pickup_time_start).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
            </p>
          )}
        </div>

        {/* Items */}
        <div className="order-detail-section">
          <h3 className="order-detail-section-title">🛍️ Items</h3>
          {(order.items || []).map((item, idx) => (
            <div key={idx} className="order-detail-item">
              <span className="order-item-qty">{item.quantity}x</span>
              <span className="order-item-name">{item.item_name || item.name}</span>
              <span className="order-item-price">{fmt(item.unit_price * item.quantity)}</span>
            </div>
          ))}
          {order.cooking_instructions && (
            <p className="order-instructions">Note: {order.cooking_instructions}</p>
          )}
        </div>

        {/* Bill */}
        <div className="order-detail-section">
          <h3 className="order-detail-section-title">💰 Bill</h3>
          {order.subtotal && <div className="bill-row"><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>}
          {order.tax_amount && <div className="bill-row"><span>Tax</span><span>{fmt(order.tax_amount)}</span></div>}
          {order.platform_fee && <div className="bill-row"><span>Platform Fee</span><span>{fmt(order.platform_fee)}</span></div>}
          {order.tip_amount > 0 && <div className="bill-row"><span>Tip</span><span>{fmt(order.tip_amount)}</span></div>}
          {order.discount_amount > 0 && <div className="bill-row discount-row"><span>Discount</span><span>-{fmt(order.discount_amount)}</span></div>}
          <hr className="price-divider" />
          <div className="bill-row total-row"><span>Total</span><span>{fmt(order.total_amount || order.total)}</span></div>
          {order.payment_method && <p className="payment-method-label">Paid via {order.payment_method.replace(/_/g, ' ')}</p>}
        </div>

        {/* Actions */}
        <div className="order-actions">
          {isCompleted && (
            <>
              <button className="btn-primary" onClick={handleReorder}>🔄 Reorder</button>
              {!ratingSubmitted && (
                <button className="btn-outline" onClick={() => setShowRatingModal(true)}>⭐ Rate Order</button>
              )}
            </>
          )}
          {canCancel && (
            <button className="btn-danger" onClick={() => setShowCancelModal(true)}>✕ Cancel Order</button>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-container modal-medium" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Cancel Order</h3>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Please tell us why you want to cancel:</p>
              <textarea
                className="form-input"
                placeholder="Enter reason..."
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={4}
              />
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button className="btn-outline" onClick={() => setShowCancelModal(false)}>Back</button>
                <button className="btn-danger" onClick={handleCancel} disabled={cancelLoading}>
                  {cancelLoading ? 'Cancelling...' : 'Cancel Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="modal-overlay" onClick={() => setShowRatingModal(false)}>
          <div className="modal-container modal-medium" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Rate Your Experience</h3>
              <button className="modal-close" onClick={() => setShowRatingModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="rating-section">
                <p className="rating-label">Food Quality</p>
                <StarRating rating={foodRating} size={32} onChange={setFoodRating} />
              </div>
              <div className="rating-section">
                <p className="rating-label">Kitchen / Service</p>
                <StarRating rating={restaurantRating} size={32} onChange={setRestaurantRating} />
              </div>
              <button
                className="btn-primary btn-full"
                onClick={handleSubmitRating}
                disabled={ratingLoading}
                style={{ marginTop: 20 }}
              >
                {ratingLoading ? 'Submitting...' : 'Submit Rating'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
